import PolarisIcon from '@/components/_common/PolarisIcon';
import { MILLISECONDS_PER_PIXEL, SEGMENT_CONTAINER_PADDING } from '@/components/Packaging/constants';
import NoStylesButton from '@/components/Packaging/LayeredTimeline/common/NoStylesButton';
import CloseOverlayGroupsMarkers from '@/components/Packaging/LayeredTimeline/overlays/CloseOverlayGroupsMarkers';
import { getOverlayWidth, isOverlayFit } from '@/components/Packaging/utils';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { selectMovingOverlayData, selectPackagingTimelineFrameDuration, selectPackagingTimelineScale } from '@/redux/selectors/packagingSelectors';
import {
  selectOverlayLayerByIndex,
  selectOverlayLayersCount,
  selectTimelineDuration,
  selectTimelineFramesCount
} from '@/redux/selectors/packagingTimelineSelectors';
import type { FrameIndex, Milliseconds, OverlaysTrayDroppableData } from '@/redux/slices/packagingSlice.types';
import { DROPPABLE_TYPES } from '@/redux/slices/packagingSlice.types';
import { removeOverlayLayer } from '@/redux/slices/packagingTimelineSlice';
import { useDroppable } from '@dnd-kit/core';
import styled from '@emotion/styled';
import { Tooltip } from '@mui/material';
import React from 'react';
import Overlay from './overlays/Overlay';

interface OverlaysTrayProps {
  index: number;
  isHighlighted: boolean;
}

const GHOST_BORDER_WIDTH = 1;

const getGhostLeftPosition = (start: FrameIndex, frameDuration: Milliseconds, scale: number) =>
  (start * frameDuration * scale) / MILLISECONDS_PER_PIXEL - 1;

const OverlaysTray = ({ index, isHighlighted }: OverlaysTrayProps) => {
  const dispatch = useAppDispatch();
  const timelineFramesCount = useAppSelector(selectTimelineFramesCount);
  const frameDuration = useAppSelector(selectPackagingTimelineFrameDuration);
  const timelineDuration = useAppSelector(selectTimelineDuration);
  const scale = useAppSelector(selectPackagingTimelineScale);
  const layer = useAppSelector((state) => selectOverlayLayerByIndex(state, index));
  const totalLayersCount = useAppSelector(selectOverlayLayersCount);
  const movingOverlayData = useAppSelector(selectMovingOverlayData);
  const data: OverlaysTrayDroppableData = {
    type: DROPPABLE_TYPES.OVERLAYS_TRAY,
    layerIndex: index
  };
  const { setNodeRef } = useDroppable({
    id: `overlay-tray-${index}`,
    data
  });
  const overlayTrayWidth = (1 / MILLISECONDS_PER_PIXEL) * timelineDuration * scale;

  const removeLayer = () => dispatch(removeOverlayLayer(index));

  let ghostOverlay: React.ReactNode = null;

  if (movingOverlayData) {
    const ghostWidth = `${getOverlayWidth(movingOverlayData.duration, frameDuration, scale) - GHOST_BORDER_WIDTH * 2}px`;

    if (movingOverlayData.newStart !== null) {
      if (movingOverlayData.targetLayerIndex === index) {
        const left = `${getGhostLeftPosition(movingOverlayData.newStart, frameDuration, scale)}px`;
        ghostOverlay = <OverlayDropGhost style={{ width: ghostWidth, left }} />;
      }
    } else if (movingOverlayData.originalLayerIndex === index && movingOverlayData.originalStart !== null) {
      const left = `${getGhostLeftPosition(movingOverlayData.originalStart, frameDuration, scale)}px`;
      ghostOverlay = <OverlayDropGhost style={{ width: ghostWidth, left }} />;
    }
  }


  return (
    <div>
      <Tab>
        <PolarisIcon icon="layers" height="15" width="15" style={{ marginRight: '0.4rem' }} />
        <TabLabel>Layer {index + 1}</TabLabel>
        {totalLayersCount > 1 && (
          <Tooltip title="Remove layer" placement="top">
            <RemoveLayerButton data-prevent-timeline-seek onClick={removeLayer}>
              <RemoveLayerIcon icon="close" width={16} height={16} />
            </RemoveLayerButton>
          </Tooltip>
        )}
      </Tab>
      <OverlaysDropArea isHighlighted={isHighlighted} style={{ width: `${overlayTrayWidth}px` }} ref={setNodeRef}>
        <RowOfItems>
          {ghostOverlay}
          {layer.map((overlay) => (
            <Overlay key={overlay.draggableId} overlay={overlay} layerIndex={index} isFit={isOverlayFit(timelineFramesCount, layer, overlay)} />
          ))}
          <CloseOverlayGroupsMarkers layerIndex={index} />
        </RowOfItems>
      </OverlaysDropArea>
    </div>
  );
};

export default React.memo(OverlaysTray);

const Tab = styled.div`
  position: sticky;
  left: 0;
  z-index: 1; // to overlay drop OverlaysDropArea's border
  min-width: 130px;
  margin-bottom: -1px;
  background: ${({ theme }) => theme.palette.lavender.main};
  border-radius: ${({ theme }) => `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0px 0px`};
  padding: ${({ theme }) => theme.spacing(0.625, 0.25, 0.625, 1.5)};
  color: ${({ theme }) => (theme.palette.mode === 'dark' ? theme.palette.common.black : theme.palette.common.white)};
  font-weight: ${({ theme }) => theme.typography.fontWeightBold};
  font-size: 1.23rem;
  display: inline-flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
`;

const TabLabel = styled.div`
  flex-grow: 1;
  font-size: 1rem;
`;

const RemoveLayerButton = styled(NoStylesButton)`
  margin-right: ${({ theme }) => theme.spacing(1)};
`;

const RemoveLayerIcon = styled(PolarisIcon)`
  display: block;
  cursor: pointer;
`;

const OVERLAYS_CONTAINER_BORDER_WIDTH = 1;

const OverlaysDropArea = styled.div<{ isHighlighted: boolean }>`
  position: relative;
  box-sizing: content-box;
  border: ${OVERLAYS_CONTAINER_BORDER_WIDTH}px solid ${({ theme }) => `${theme.palette.lavender.main}80`};
  border-radius: ${({ theme }) => `${theme.shape.borderRadius}px`};
  padding: ${({ theme }) => theme.spacing(0.625, 0, 0.75, 0)};
  // the amount by which overlays offset left inside tray should be the same as in Segments tray, that's why padding-left depend on SEGMENT_CONTAINER_PADDING
  padding-left: ${SEGMENT_CONTAINER_PADDING - OVERLAYS_CONTAINER_BORDER_WIDTH}px;

  ${({ theme, isHighlighted }) => {
    if (isHighlighted) {
      return `background: ${theme.palette.success.main}4a;`;
    }

    if (theme.palette.mode === 'dark') {
      return `background: rgba(0, 0, 0, 0.30);`;
    }

    return 'background: rgba(235, 240, 255, 0.90);';
  }};
`;

const RowOfItems = styled.div`
  position: relative;
  height: 55px;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
`;

const OverlayDropGhost = styled.div`
  position: absolute;
  height: 100%;
  border: ${GHOST_BORDER_WIDTH}px solid #d082ff60;
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
`;
